import { InternalAxiosRequestConfig } from 'axios';
import { useSpaceStore } from '@/shared/store/space-store';

/**
 * URL 패턴 매처 타입 정의
 */
type Matcher = (url: string, method?: string) => boolean;

/**
 * 정확한 URL과 메서드 매칭 헬퍼
 */
const matchExact =
  (expectedMethod: string, expectedUrl: string): Matcher =>
  (url, method) =>
    method === expectedMethod && url === expectedUrl;

/**
 * 정규식 패턴과 메서드 매칭 헬퍼
 */
const matchPattern =
  (expectedMethod: string, pattern: RegExp): Matcher =>
  (url, method) =>
    method === expectedMethod && pattern.test(url);

/**
 * X-Space-Id 헤더를 추가할 URL 패턴 매처들
 */
const spaceIdMatchers: Matcher[] = [
  matchExact('POST', '/title/generate'),
  matchExact('POST', '/v1.1/tts/generate'),
  matchExact('POST', '/v1.1/tts/generate/as-asset'),
  matchExact('POST', '/v1.0/ai/image-editor/generate'),
  matchPattern('PATCH', /^\/v1\.0\/project\/[^/]+\/setting$/),
  matchPattern('POST', /^\/v1\.0\/project\/[^/]+\/setting$/),
  matchExact('POST', '/v1.0/rendering/video'),
  matchExact('PATCH', '/v1.0/admin/point/expire'),
  matchPattern('PATCH', /^\/v1\.1\/content\/download\/pre-signed\/[^/]+$/),
  // Content Upload APIs
  matchExact('PATCH', '/v1.0/content/upload/complete'),
  // 세션 컨텍스트의 billing 은 space 에 확정된 provider 를 따른다
  matchExact('GET', '/v1.0/session/context'),
  // 상품 단건 조회도 목록과 같은 기준으로 통화를 정해야 한다. space 를 안 보내면
  // 서버가 접속 IP 로만 판정해서, 목록은 원화인데 확인 모달만 달러로 어긋난다.
  matchPattern('GET', /^\/v1\.0\/product\/point\/[^/]+$/),
  matchPattern('GET', /^\/v1\.0\/product\/plan\/[^/]+$/),
  // Payment APIs
  matchExact('POST', '/v1.0/payments/billing/confirm'),
  matchExact('POST', '/v1.0/payments/billing/auth/toss'),
  matchExact('GET', '/v1.0/payments/billing/auth/toss/info'),
  matchExact('GET', '/v1.0/payments/billing/status'),
  matchExact('POST', '/v1.0/payments/refund'),
  matchExact('POST', '/v1.0/payments/confirm'),
  matchExact('POST', '/v1.0/payments/confirm/complete'),
  matchExact('POST', '/v1.0/payments/retry'),
  matchExact('GET', '/v1.0/payments/result'),
  matchExact('GET', '/v1.0/payments'),
  matchExact('GET', '/v1.0/payments/refund/calculate'),
  matchExact('PUT', '/v1.0/subscription/plan/scheduled-change'),
  // Rendering Status APIs
  matchExact('GET', '/v1.0/rendering/summary'),
  matchExact('PATCH', '/v1.0/rendering/read'),
  // Social Connection APIs
  matchPattern('POST', /^\/v1\.0\/connections\/youtube\/connect/),
  matchPattern('POST', /^\/v1\.0\/connections\/youtube\/upload/),
];

/**
 * 특정 URL에 X-Space-Id 헤더를 추가하는 인터셉터
 * - 패턴에 매칭되는 URL에만 X-Space-Id 헤더 추가
 */
export const attachSpaceIdHeader = (
  config: InternalAxiosRequestConfig,
): void => {
  const url = config.url ?? '';
  const method = config.method?.toUpperCase();
  const shouldAttachSpaceId = spaceIdMatchers.some((fn) => fn(url, method));
  const spaceId = useSpaceStore.getState().spaceId;

  if (shouldAttachSpaceId && spaceId) {
    config.headers['X-Space-Id'] = spaceId;
  }
};
